import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Group as GroupIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

// Move FormSection outside to prevent re-renders
const FormSection = ({ title, description, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ color: 'primary.light', mb: 1 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {description}
      </Typography>
    )}
    {children}
  </Box>
);

const ERPForm = () => {
  const [formData, setFormData] = useState({
    // Company Information
    company_name: '',
    primary_contact_email: '',
    primary_contact_name: '',
    phone: '',
    industry: '',
    employees: '',
    departments: '',
    
    // CRM Requirements
    crm_data_management: '',
    crm_client_portal: '',
    crm_categories: '',
    crm_reports: '',
    
    // Project Management
    projects_current_tool: '',
    projects_client_visibility: '',
    projects_time_tracking: '',
    projects_workflows: '',
    
    // Accounting & Invoicing
    accounting_billing_method: '',
    accounting_subscription: '',
    accounting_tax: '',
    
    // Files / Cloud Storage
    files_types: '',
    files_sharing: '',
    files_versioning: '',
    
    // Automation & Notifications
    automation_needs: '',
    notifications: '',
    
    // Access Control & Permissions
    access_roles: '',
    client_restrictions: '',
    access_actions: '',
    
    // Attachments & Additional Notes
    additional_notes: '',
  });

  const [roleActions, setRoleActions] = useState([]);
  const [currentRole, setCurrentRole] = useState('');
  const [currentAction, setCurrentAction] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setFormData(prevData => ({
      ...prevData,
      [field]: value,
    }));
  }, []);

  // Individual handlers for critical fields
  const handleCompanyNameChange = useCallback((event) => {
    setFormData(prev => ({ ...prev, company_name: event.target.value }));
  }, []);

  const handleEmailChange = useCallback((event) => {
    setFormData(prev => ({ ...prev, primary_contact_email: event.target.value }));
  }, []);

  const handleContactNameChange = useCallback((event) => {
    setFormData(prev => ({ ...prev, primary_contact_name: event.target.value }));
  }, []);

  const addRoleAction = useCallback(() => {
    if (currentRole && currentAction) {
      const role = currentRole === '__custom' ? customRole : currentRole;
      const newAction = { role, action: currentAction, id: Date.now() };
      setRoleActions(prevActions => [...prevActions, newAction]);
      setCurrentAction('');
    }
  }, [currentRole, currentAction, customRole]);

  const removeRoleAction = useCallback((id) => {
    setRoleActions(prevActions => prevActions.filter(action => action.id !== id));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitStatus('submitting');
    
    // Prepare form data for Formspree
    const formDataToSubmit = new FormData();
    
    // Add all form fields (only non-empty values)
    Object.keys(formData).forEach(key => {
      if (formData[key] && formData[key].trim() !== '') {
        formDataToSubmit.append(key, formData[key].trim());
      }
    });
    
    // Add role actions
    const roleActionsString = roleActions.map(action => `${action.role}: ${action.action}`).join('; ');
    if (roleActionsString && roleActionsString.trim() !== '') {
      formDataToSubmit.append('access_actions', roleActionsString);
    }
    
    // Note: File uploads disabled - Formspree doesn't allow file uploads on this endpoint
    // Users can describe files in the additional notes field instead
    
    // Add metadata (only if email exists)
    if (formData.primary_contact_email && formData.primary_contact_email.trim() !== '') {
      formDataToSubmit.append('_replyto', formData.primary_contact_email.trim());
    }
    formDataToSubmit.append('_subject', 'New Cybric ERP Requirement Submission');
    formDataToSubmit.append('submitted_by', new Date().toISOString());

    try {
      const response = await fetch('https://formspree.io/f/xeorvdlz', {
        method: 'POST',
        body: formDataToSubmit,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitStatus('success');
        setShowSuccessModal(true);
        // Reset form after successful submission
        setFormData({
          company_name: '',
          primary_contact_email: '',
          primary_contact_name: '',
          phone: '',
          industry: '',
          employees: '',
          departments: '',
          crm_data_management: '',
          crm_client_portal: '',
          crm_categories: '',
          crm_reports: '',
          projects_current_tool: '',
          projects_client_visibility: '',
          projects_time_tracking: '',
          projects_workflows: '',
          accounting_billing_method: '',
          accounting_subscription: '',
          accounting_tax: '',
          files_types: '',
          files_sharing: '',
          files_versioning: '',
          automation_needs: '',
          notifications: '',
          access_roles: '',
          client_restrictions: '',
          access_actions: '',
          additional_notes: '',
        });
        setRoleActions([]);
        setCurrentRole('');
        setCurrentAction('');
        setCustomRole('');
      } else {
        const data = await response.json();
        console.error('Formspree response:', response.status, response.statusText);
        console.error('Formspree errors:', data.errors);
        console.error('Formspree data:', data);
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Submission error:', error);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(1200px 600px at 10% 10%, rgba(11,20,39,0.35), transparent), linear-gradient(180deg, #071129, #0b2a52)',
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        <Card sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* <Box
                component="img"
                src="/Cybric-Logo.png"
                alt="Cybric Logo"
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: 'contain',
                }}
              /> */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="img"
                    src="/Cybric-Logo.png"
                    alt="Cybric"
                    sx={{
                      height: 40,
                      objectFit: 'contain',
                    }}
                  />
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.light' }}>
                    ERP
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Requirement Gathering — Implementation Form
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'right' }}>
              From Stress & Chaos → To Control & Growth
            </Typography>
          </Box>


          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 3 }}>
              There was an error submitting the form. Please try again.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Company Information */}
            <FormSection title="Company Information">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={formData.company_name}
                    onChange={handleCompanyNameChange}
                    placeholder="Acme Construction Ltd"
                    required
                    InputProps={{
                      startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Primary Contact Email"
                    type="email"
                    value={formData.primary_contact_email}
                    onChange={handleEmailChange}
                    placeholder="name@example.com"
                    required
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name (Primary Contact)"
                    value={formData.primary_contact_name}
                    onChange={handleContactNameChange}
                    placeholder="John Doe"
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    placeholder="+234 70x xxx xxxx"
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Industry / Sector"
                    value={formData.industry}
                    onChange={handleInputChange('industry')}
                    placeholder="Real Estate / Construction"
                    InputProps={{
                      startAdornment: <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Number of Employees"
                    value={formData.employees}
                    onChange={handleInputChange('employees')}
                    placeholder="e.g., 120"
                    InputProps={{
                      startAdornment: <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Departments"
                    value={formData.departments}
                    onChange={handleInputChange('departments')}
                    placeholder="Sales, Projects, Finance"
                    helperText="Separate with commas."
                  />
                </Grid>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4 }} />

            {/* CRM Requirements */}
            <FormSection title="CRM Requirements" description="How you manage customer relationships">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Data Management</InputLabel>
                    <Select
                      value={formData.crm_data_management}
                      onChange={handleInputChange('crm_data_management')}
                      label="Data Management"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Manual (Spreadsheets)">Manual (Spreadsheets)</MenuItem>
                      <MenuItem value="CRM Software">CRM Software</MenuItem>
                      <MenuItem value="Hybrid">Hybrid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Client Portal Access</InputLabel>
                    <Select
                      value={formData.crm_client_portal}
                      onChange={handleInputChange('crm_client_portal')}
                      label="Client Portal Access"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Full Access">Full Access</MenuItem>
                      <MenuItem value="Limited Access">Limited Access</MenuItem>
                      <MenuItem value="No Portal">No Portal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Do you use Leads / Prospects / Clients categories?</InputLabel>
                    <Select
                      value={formData.crm_categories}
                      onChange={handleInputChange('crm_categories')}
                      label="Do you use Leads / Prospects / Clients categories?"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                      Assign primary contacts to Prospects/Clients to enable portal logins & notifications.
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Key CRM Reports Required"
                    value={formData.crm_reports}
                    onChange={handleInputChange('crm_reports')}
                    placeholder="e.g., sales pipeline, conversion, customer history"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4 }} />

            {/* Project Management */}
            <FormSection title="Project Management" description="Plan and track your projects">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Tool"
                    value={formData.projects_current_tool}
                    onChange={handleInputChange('projects_current_tool')}
                    placeholder="e.g., Excel, Asana"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Client Visibility</InputLabel>
                    <Select
                      value={formData.projects_client_visibility}
                      onChange={handleInputChange('projects_client_visibility')}
                      label="Client Visibility"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Yes — Full">Yes — Full</MenuItem>
                      <MenuItem value="Yes — Limited">Yes — Limited</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Time / Billable Hours</InputLabel>
                    <Select
                      value={formData.projects_time_tracking}
                      onChange={handleInputChange('projects_time_tracking')}
                      label="Time / Billable Hours"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Approvals & Workflows to automate"
                    value={formData.projects_workflows}
                    onChange={handleInputChange('projects_workflows')}
                    placeholder="e.g., project approval, expense approvals"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4 }} />

            {/* Accounting & Invoicing */}
            <FormSection title="Accounting & Invoicing" description="Billing, payments, and financial tracking">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Billing Method</InputLabel>
                    <Select
                      value={formData.accounting_billing_method}
                      onChange={handleInputChange('accounting_billing_method')}
                      label="Billing Method"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Manual">Manual</MenuItem>
                      <MenuItem value="Automated">Automated</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Recurring / Subscription Billing</InputLabel>
                    <Select
                      value={formData.accounting_subscription}
                      onChange={handleInputChange('accounting_subscription')}
                      label="Recurring / Subscription Billing"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tax / VAT Configuration"
                    value={formData.accounting_tax}
                    onChange={handleInputChange('accounting_tax')}
                    placeholder="e.g., VAT rate, tax registration"
                  />
                </Grid>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4 }} />

            {/* Files / Cloud Storage */}
            <FormSection title="Files / Cloud Storage" description="Document management and sharing">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Types of Files You Store"
                    value={formData.files_types}
                    onChange={handleInputChange('files_types')}
                    placeholder="e.g., contracts, plans, images, videos"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Share files with clients/partners?</InputLabel>
                    <Select
                      value={formData.files_sharing}
                      onChange={handleInputChange('files_sharing')}
                      label="Share files with clients/partners?"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Require versioning / approvals?</InputLabel>
                    <Select
                      value={formData.files_versioning}
                      onChange={handleInputChange('files_versioning')}
                      label="Require versioning / approvals?"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4 }} />

            {/* Automation & Notifications */}
            <FormSection title="Automation & Notifications" description="What would you like to automate?">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Automations Needed"
                    value={formData.automation_needs}
                    onChange={handleInputChange('automation_needs')}
                    placeholder="e.g., auto-invoice on completion, follow-up reminders"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Email Notifications</InputLabel>
                    <Select
                      value={formData.notifications}
                      onChange={handleInputChange('notifications')}
                      label="Email Notifications"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4 }} />

            {/* Access Control & Permissions */}
            <FormSection title="Access Control & Permissions" description="Who should be able to do what?">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User Roles"
                    value={formData.access_roles}
                    onChange={handleInputChange('access_roles')}
                    placeholder="e.g., Admin, Manager, Staff, Client"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Restricted Client Access</InputLabel>
                    <Select
                      value={formData.client_restrictions}
                      onChange={handleInputChange('client_restrictions')}
                      label="Restricted Client Access"
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Invoices only">Invoices only</MenuItem>
                      <MenuItem value="Project view only">Project view only</MenuItem>
                      <MenuItem value="Full">Full</MenuItem>
                      <MenuItem value="None">None</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Actions per Role
                  </Typography>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Select role</InputLabel>
                        <Select
                          value={currentRole}
                          onChange={(e) => setCurrentRole(e.target.value)}
                          label="Select role"
                        >
                          <MenuItem value="">Select role</MenuItem>
                          <MenuItem value="Admin">Admin</MenuItem>
                          <MenuItem value="Manager">Manager</MenuItem>
                          <MenuItem value="Staff">Staff</MenuItem>
                          <MenuItem value="Client">Client</MenuItem>
                          <MenuItem value="__custom">Custom…</MenuItem>
                        </Select>
                      </FormControl>
                      {currentRole === '__custom' && (
                        <TextField
                          fullWidth
                          label="Enter custom role"
                          value={customRole}
                          onChange={(e) => setCustomRole(e.target.value)}
                          sx={{ mt: 2 }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <TextField
                          fullWidth
                          label="Type an allowed action"
                          value={currentAction}
                          onChange={(e) => setCurrentAction(e.target.value)}
                          placeholder="e.g., approve invoices"
                        />
                        <Button
                          variant="outlined"
                          onClick={addRoleAction}
                          startIcon={<AddIcon />}
                          sx={{ mt: 1 }}
                        >
                          Add
                        </Button>
                      </Stack>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                        Select a role, type an action, then click Add. Repeat as needed.
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    {roleActions.map((action) => (
                      <Chip
                        key={action.id}
                        label={`${action.role}: ${action.action}`}
                        onDelete={() => removeRoleAction(action.id)}
                        deleteIcon={<DeleteIcon />}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4 }} />

            {/* Attachments & Additional Notes */}
            <FormSection title="Attachments & Additional Notes" description="Note: File uploads are not available with this form. Please describe your files in the notes below or email them separately.">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="File Descriptions"
                    placeholder="Describe any files you would like to share (e.g., 'Sample invoice template', 'Current project report', 'Company logo files')"
                    helperText="Since file uploads aren't available, please describe the files you'd like to share. You can email them separately to us."
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Additional Notes"
                    value={formData.additional_notes}
                    onChange={handleInputChange('additional_notes')}
                    placeholder="Any special requests, integrations, or additional information..."
                    InputProps={{
                      startAdornment: <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
              </Grid>
            </FormSection>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitStatus === 'submitting'}
                sx={{ px: 4, py: 2 }}
              >
                {submitStatus === 'submitting' ? 'Submitting...' : 'Submit Requirements'}
              </Button>
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
              We'll review your requirements and get back to you within 24 hours.
            </Typography>
          </form>

          <Typography variant="caption" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
            We respect your privacy — data is transmitted over HTTPS and will be used only for ERP implementation purposes.
          </Typography>
        </Card>
      </Container>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
            backdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.04)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography variant="h5" sx={{ color: 'primary.light', fontWeight: 600 }}>
              Form Submitted Successfully!
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 2 }}>
            Thank you for submitting your ERP requirements. We've received your information and will review it carefully.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Our team will get back to you within 24 hours to discuss next steps and answer any questions you may have.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setShowSuccessModal(false)}
            variant="contained"
            size="large"
            sx={{ px: 4 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ERPForm;
